import React from 'react'
import { observable, reaction, makeObservable } from 'mobx'
import { observer, inject } from 'mobx-react'
import { Button, Form, Item, TextArea } from 'semantic-ui-react'
import { toast } from 'react-toastify'
import moment from 'moment'
import styled from 'styled-components'

const SummaryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`

const SummaryLabel = styled.label`
  font-weight: bold;
  font-size: 1em;
`

const RefreshButton = styled(Button).attrs({ basic: true, size: 'mini', compact: true })`
  width: auto !important;
  flex-shrink: 0;
`

const SummaryFooter = styled.div`
  margin-top: 8px;
  font-size: 0.85em;
  color: #888;
`

const LoadingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  background: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
`

const Spinner = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid #e0e0e0;
  border-top-color: #36547c;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 12px;
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`

const LoadingText = styled.span`
  color: #888;
  font-size: 0.9em;
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  background: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 20px;
  text-align: center;
`

const EmptyIcon = styled.div`
  width: 48px;
  height: 48px;
  border: 2px dashed #ccc;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  color: #bbb;
  font-size: 1.4em;
`

const EmptyTitle = styled.div`
  font-weight: bold;
  font-size: 1em;
  color: #333;
  margin-bottom: 6px;
`

const EmptyDescription = styled.div`
  color: #888;
  font-size: 0.85em;
  line-height: 1.4;
`

class ConversationSummary extends React.Component {
  loading = false

  state = {
    summary: '',
    lastUpdated: null,
  }

  constructor(props) {
    super(props)

    makeObservable(this, {
      loading: observable
    })
  }

  componentDidMount() {
    const { store } = this.props
    // store.conversation is a computed lookup keyed by conversationUid, so react on
    // conversationUid directly rather than a field on conversation, which can lag.
    this.dispose = reaction(() => store.conversationUid, conversationUid => {
      if (!conversationUid) {
        this.setState({ summary: '', lastUpdated: null })
        return
      }
      this.refreshSummary()
    }, {
      fireImmediately: true,
    })
  }

  componentWillUnmount() {
    this.dispose()
  }

  refreshSummary = async(forceRefresh = false) => {
    const { actions, store } = this.props
    const { conversationUid } = store
    if (!conversationUid) {
      return
    }

    this.loading = true
    const [response, error] = await actions.agent.getConversationSummary(conversationUid, forceRefresh)
    this.loading = false

    if (error) {
      toast.error('We couldn\'t generate a conversation summary, please try again')
      return
    }

    this.setState({
      summary: response.summary,
      lastUpdated: response.generatedAt ? new Date(response.generatedAt * 1000) : new Date(),
    })
  }

  render() {
    return (
      <Item.Group className="chat-notes">
        <Form size="tiny">
          <SummaryHeader>
            <SummaryLabel>Summary</SummaryLabel>
            <RefreshButton type="button" onClick={() => this.refreshSummary(true)}>
              ↻ Refresh
            </RefreshButton>
          </SummaryHeader>
          {this.loading ? (
            <LoadingOverlay>
              <Spinner />
              <LoadingText>Generating summary...</LoadingText>
            </LoadingOverlay>
          ) : this.state.summary ? (
            <Form.Field>
              <TextArea
                rows={15}
                value={this.state.summary}
                readOnly
              />
            </Form.Field>
          ) : (
            <EmptyState>
              <EmptyIcon>💬</EmptyIcon>
              <EmptyTitle>No summary yet</EmptyTitle>
              <EmptyDescription>
                This conversation has no messages to summarise yet. A summary will appear automatically once the conversation begins.
              </EmptyDescription>
            </EmptyState>
          )}
          <SummaryFooter>
            {this.state.lastUpdated && (
              <div>Auto-generated · last updated {moment(this.state.lastUpdated).format('HH:mm a')}</div>
            )}
            {this.loading && <div>Regenerating from latest messages...</div>}
            {this.state.summary
              ? <div>Summary regenerates automatically as the conversation updates. Use Refresh to regenerate on demand.</div>
              : <div>No data · nothing to summarise</div>
            }
          </SummaryFooter>
        </Form>
      </Item.Group>
    )
  }
}

ConversationSummary.propTypes = {}

export default inject('store', 'actions')(observer(ConversationSummary))
